import React, { useEffect } from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Paper,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import * as Yup from "yup";
import { Classroom, Pace } from "../../types/ClassroomModel";
import { EapProgram } from "../../types/School";
import { Teacher } from "../../types/TeacherModel";
import EntityAutocomplete from "../EntityAutocomplete";
import {
  calculateEndDate,
  formatDateForDisplay,
  getTodayStart,
  toLocalDate,
} from "../../utils/dateUtils";

const PROGRAM_WEEKS: Record<string, number> = {
  [EapProgram.EAP_YELLOW]: 20,
  [EapProgram.EAP_GREEN]: 20,
  [EapProgram.EAP_ACCESS]: 12,
};

export interface ClassroomFormProps {
  selectedRow?: Classroom;
  teachers: Teacher[];
  onSubmit: (values: Classroom) => Promise<void>;
}

const ClassroomForm: React.FC<ClassroomFormProps> = ({
  selectedRow,
  teachers,
  onSubmit,
}) => {
  const formik = useFormik({
    initialValues: {
      ...(selectedRow?._id ? { _id: selectedRow._id } : {}),
      classroomName: selectedRow?.classroomName || "",
      program: selectedRow?.program || EapProgram.EAP_YELLOW,
      startDate: selectedRow
        ? toLocalDate(selectedRow.startDate) || new Date()
        : new Date(),
      endDate: selectedRow
        ? toLocalDate(selectedRow.endDate) || new Date()
        : new Date(),
      pace: selectedRow?.pace || (7 as Pace),
      isActive: selectedRow?.isActive ?? true,
      students: selectedRow?.students || [],
      teacher: selectedRow?.teacher || undefined,
      teacherId: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      classroomName: Yup.string().required("Required"),
      program: Yup.string().oneOf(Object.values(EapProgram)).required("Required"),
      startDate: Yup.date().required("Required"),
      pace: Yup.number().oneOf([3, 7, 14]).required("Required"),
      isActive: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      await onSubmit(values as unknown as Classroom);
      setTimeout(() => {
        formik.resetForm({ values });
      }, 0);
    },
  });

  // Auto-fill classroom name when program changes
  useEffect(() => {
    if (formik.values.program && !formik.values.classroomName) {
      const name = formik.values.program.replace("EAP_", "").replace("_", " ");
      formik.setFieldValue("classroomName", name);
    }
  }, [formik.values.program]);

  const getEndDate = (): Date => {
    const weeks = PROGRAM_WEEKS[formik.values.program] || 20;
    return calculateEndDate(formik.values.startDate, formik.values.pace, weeks) || new Date();
  };

  const handleTeacherSelect = (selectedTeacher: Teacher) => {
    if (selectedTeacher) {
      formik.setFieldValue("teacherId", selectedTeacher._id);
      formik.setFieldValue("teacher", {
        id: selectedTeacher._id,
        name: selectedTeacher.name,
        email: selectedTeacher.email,
      });
    }
  };

  const hasTeacher = formik.values.teacher?.name;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Teacher selector */}
          <Grid item xs={12}>
            {hasTeacher ? (
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#e3f2fd",
                }}
              >
                <span style={{ fontWeight: "bold", marginRight: 10 }}>
                  Teacher:
                </span>
                <span style={{ flex: 1 }}>
                  {formik.values.teacher?.name} ({formik.values.teacher?.email})
                </span>
              </Paper>
            ) : (
              <EntityAutocomplete
                entityName="Select Teacher"
                entityOptions={teachers.filter((t) => t.isActive)}
                onEntitySelect={handleTeacherSelect}
                renderDisplay={(props, teacher) => (
                  <li key={teacher._id} {...props}>
                    {teacher.name} — {teacher.email}
                  </li>
                )}
              />
            )}
          </Grid>

          {/* Program */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="program-label">Program</InputLabel>
              <Select
                labelId="program-label"
                id="program"
                name="program"
                value={formik.values.program}
                label="Program"
                onChange={(e) => formik.setFieldValue("program", e.target.value)}
              >
                {Object.values(EapProgram).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p.replace("EAP_", "").replace("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Classroom Name */}
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="classroomName"
              name="classroomName"
              label="Classroom Name"
              value={formik.values.classroomName}
              onChange={formik.handleChange}
              error={formik.touched.classroomName && Boolean(formik.errors.classroomName)}
              helperText={formik.touched.classroomName && formik.errors.classroomName}
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Start Date"
              value={dayjs(formik.values.startDate)}
              onChange={(value) =>
                formik.setFieldValue("startDate", value ? value.toDate() : null)
              }
              minDate={dayjs(getTodayStart())}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: formik.touched.startDate && Boolean(formik.errors.startDate),
                  helperText:
                    formik.touched.startDate && typeof formik.errors.startDate === "string"
                      ? formik.errors.startDate
                      : undefined,
                },
              }}
            />
          </Grid>

          {/* End Date (auto-calculated) */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              label="End Date"
              value={formatDateForDisplay(getEndDate(), "dd-MM-yyyy")}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Pace */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="pace-label">Pace</InputLabel>
              <Select
                labelId="pace-label"
                id="pace"
                name="pace"
                value={formik.values.pace}
                label="Pace"
                onChange={(e) => formik.setFieldValue("pace", e.target.value)}
              >
                {([3, 7, 14] as Pace[]).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p} days
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Active */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                />
              }
              label={formik.values.isActive ? "Active" : "Disabled"}
              labelPlacement="start"
            />
          </Grid>
        </Grid>

        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting}
          style={{ marginTop: "2rem" }}
        >
          {selectedRow?._id ? "Update" : "Create"} Classroom
        </Button>
      </form>
    </LocalizationProvider>
  );
};

export default ClassroomForm;
